import { useEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { products } from '@/services/mock-data';
import { useProductTheme } from '@/providers/theme-provider';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Smartphone, Home, Bell, User, Search } from 'lucide-react';

export default function MobileProductLayout() {
    const { productSlug } = useParams<{ productSlug: string }>();
    const navigate = useNavigate();
    const { setTheme } = useProductTheme();
    const product = products.find(p => p.slug === productSlug);

    useEffect(() => {
        if (product) {
            if (product.slug === 'banking-app') {
                setTheme('banking');
            } else if (product.slug === 'bbps-mobile') {
                setTheme('vendor');
            } else if (product.slug === 'circle-collect') {
                setTheme('analytics');
            }
        }

        return () => setTheme('global');
    }, [product, setTheme]);

    if (!product) return null;

    return (
        <div className="min-h-[calc(100vh-4rem)] bg-muted/30 flex flex-col items-center justify-center p-4 sm:p-8">
            {/* Header / Back Button */}
            <div className="w-full max-w-md flex justify-between items-center mb-6">
                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate('/')}
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                </Button>
                <div className="flex items-center gap-2">
                    <product.icon className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">{product.name} Mobile</span>
                </div>
                <div className="w-20" /> {/* Spacer */}
            </div>

            {/* Mobile Device Mockup */}
            <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[700px] w-[340px] shadow-2xl overflow-hidden">
                {/* Device Camera/Notch */}
                <div className="h-[32px] w-[3px] bg-gray-800 absolute -start-[17px] top-[72px] rounded-s-lg"></div>
                <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
                <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
                <div className="h-[64px] w-[3px] bg-gray-800 absolute -end-[17px] top-[142px] rounded-e-lg"></div>

                {/* Screen Content */}
                <div className="rounded-[1.7rem] overflow-hidden w-full h-full bg-background flex flex-col">
                    {/* Status Bar */}
                    <div className="h-10 flex items-center justify-between px-6 pt-2">
                        <span className="text-xs font-bold">9:41</span>
                        <div className="flex gap-1.5 items-center">
                            <div className="w-4 h-2.5 border border-foreground/30 rounded-sm relative">
                                <div className="absolute right-[-2px] top-1/2 -translate-y-1/2 w-0.5 h-1 bg-foreground/30 rounded-full" />
                            </div>
                        </div>
                    </div>

                    {/* App Header */}
                    <div className="px-6 py-4 flex justify-between items-center bg-primary text-primary-foreground">
                        <div className="flex items-center gap-2">
                            <product.icon className="h-5 w-5" />
                            <span className="font-bold text-sm tracking-tight">{product.name}</span>
                        </div>
                        <Bell className="h-4 w-4 opacity-80" />
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 overflow-auto bg-muted/10">
                        <Outlet />
                    </div>

                    {/* Bottom Tab Bar */}
                    <div className="h-20 bg-background border-t flex justify-around items-center px-4 pb-4">
                        <div className="flex flex-col items-center gap-1 text-primary">
                            <Home className="h-5 w-5" />
                            <span className="text-[10px] font-medium">Home</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 text-muted-foreground/60">
                            <Search className="h-5 w-5" />
                            <span className="text-[10px] font-medium">Search</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 text-muted-foreground/60">
                            <Smartphone className="h-5 w-5" />
                            <span className="text-[10px] font-medium">Cards</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 text-muted-foreground/60">
                            <User className="h-5 w-5" />
                            <span className="text-[10px] font-medium">Profile</span>
                        </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="h-1.5 w-32 bg-foreground/20 rounded-full mx-auto mb-2" />
                </div>
            </div>

            {/* Product Meta Info */}
            <div className="mt-8 text-center max-w-sm">
                <h2 className="text-xl font-bold mb-2">Interactive Workflow</h2>
                <p className="text-sm text-muted-foreground">
                    This is a high-fidelity mobile mockup of the {product.name} onboarding journey.
                    Click through the UI to see the transition effects.
                </p>
            </div>
        </div>
    );
}
